import React from 'react';
import get from 'lodash/get';
import ReactMarkdown from 'react-markdown';
import {
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiExternalLinkLine,
} from '@remixicon/react';
import components from '../../molecules/Markdown/components';
import { translate } from '../../../localization';
import styles from './BrandDetails.module.css';
import useTruncatableText from '../../../hooks/useTruncatableText';
import NoDataInline, { NoDataInlineVariant } from '../NoDataInline';
import { PrescribingInfo } from '../../../store/markettingContentGeneration/stateSegments/brandsInformationStateSegment';

interface BrandDetailsProps {
  title: string;
  text: string | JSX.Element;
  isLink?: boolean;
  piLinks?: PrescribingInfo[];
  isForPatient?: boolean;
  classes?: string;
  initialHeight?: number;
}

const BrandDetails: React.FC<BrandDetailsProps> = ({
  title,
  text,
  isLink = false,
  piLinks,
  isForPatient = false,
  classes = '',
  initialHeight = 60,
}) => {
  const { textRef, isExpanded, isTruncated, toggleExpansion } =
    useTruncatableText(initialHeight);

  /**
   * Checks if all items in the array have a falsy `link` value.
   * @param {PrescribingInfo[]} items - The array of items to check.
   * @returns {boolean} - Returns `true` if all `link` values are falsy, otherwise `false`.
   */
  const areAllLinksFalsy = (items: PrescribingInfo[]): boolean =>
    items?.every(item => !item.link);
  return (
    <div className={`${styles.brandDetailsContainer} ${classes}`}>
      <h3 className={styles.title}>{title}</h3>
      {!isLink && (
        <>
          <div
            ref={textRef}
            className={`${styles.text} ${isExpanded ? styles.expanded : ''}`}
            style={{ maxHeight: `${initialHeight}px` }}
          >
            {text && text != null ? (
              <>
                {typeof text === 'string' ? (
                  <ReactMarkdown components={components}>{text}</ReactMarkdown>
                ) : (
                  text
                )}
              </>
            ) : (
              <NoDataInline variant={NoDataInlineVariant.LARGE_INLINE_FIELD} />
            )}
          </div>

          {/* Show the toggle button only if `text` exists, and truncation is applied or `text` is expanded */}
          {text && text != null && (isTruncated || isExpanded) && (
            <button
              onClick={toggleExpansion}
              className={styles.button}
              type='button'
            >
              {isExpanded ? translate('show_less') : translate('show_more')}
              {isExpanded ? (
                <RiArrowUpSLine
                  className={styles.icon}
                  color='#0000C9'
                  size={14}
                />
              ) : (
                <RiArrowDownSLine
                  className={styles.icon}
                  color='#0000C9'
                  size={14}
                />
              )}
            </button>
          )}
        </>
      )}

      {isLink && (
        <>
          {!piLinks ||
          piLinks?.length === 0 ||
          areAllLinksFalsy(piLinks || []) ? (
            <>
              {isForPatient ? (
                <NoDataInline
                  variant={NoDataInlineVariant.CUSTOM_MESSAGE}
                  customMessage={translate('not_applicable_message')}
                />
              ) : (
                <NoDataInline variant={NoDataInlineVariant.LINK_INLINE_FIELD} />
              )}
            </>
          ) : (
            <div className={styles.piLinksWrapper}>
              {piLinks?.map((link, index) => {
                const linkId = get(link, 'id', '');
                const linkUrl = get(link, 'link', '');
                const linkLabel = get(link, 'label', '');

                return (
                  <>
                    {link && linkUrl && linkUrl != null && (
                      <div key={linkId} className={styles.linkContainer}>
                        {linkLabel && linkLabel != null && (
                          <span className={styles.linkLabel}>{linkLabel}</span>
                        )}

                        <a
                          href={linkUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          className={styles.link}
                          key={`Link ${index + 1}`}
                        >
                          <RiExternalLinkLine
                            className={`${styles.icon} ${styles.linkIcon}`}
                            color='#0000C9'
                            size={20}
                          />
                        </a>
                      </div>
                    )}
                  </>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BrandDetails;
